import type { Event } from '@casetrace/shared';
import type { CardPosition } from '../use-case-timeline';
import type { EventAttachment } from '../event-attachments';
import { attachmentLineLabel, eventAttachments } from '../event-attachments';
import { TYPE_ICONS, TYPE_COLORS, TYPE_LABELS } from './event-type-config';
import type { ToggleHighlightHandler } from '../../../hooks/use-toggle-highlight';
import { EventStarButton } from './event-star-button';
import { EventCardCollapsed } from './event-card-collapsed';
import { EventCardExpandedPanel } from './event-card-expanded-panel';

const POSITION_CLASS: Record<CardPosition, string> = {
  only: 'rounded-lg mt-2 mb-1',
  first: 'rounded-t-lg rounded-b-none mt-2',
  middle: 'rounded-none -mt-px',
  last: 'rounded-t-none rounded-b-lg -mt-px mb-1',
};

interface TimelineEventCardProps {
  event: Event;
  isSelected: boolean;
  isHighlighted: boolean;
  canToggleHighlight: boolean;
  position: CardPosition;
  expandedEvent: Event | undefined;
  onSelect: (event: Event) => void;
  onDeselect: () => void;
  onToggleHighlight: ToggleHighlightHandler;
}

function attachmentsForCollapsedCard(attachments: EventAttachment[]): EventAttachment[] {
  return attachments.slice(0, 5);
}

export function TimelineEventCard({
  event,
  isSelected,
  isHighlighted,
  canToggleHighlight,
  position,
  expandedEvent,
  onSelect,
  onDeselect,
  onToggleHighlight,
}: TimelineEventCardProps) {
  const Icon = TYPE_ICONS[event.type];
  const color = TYPE_COLORS[event.type];
  const label = TYPE_LABELS[event.type];
  const attachments = eventAttachments(event);
  const attachmentsLabel = attachmentLineLabel(attachments, event.contentPreview);
  const collapsedAttachments = attachmentsForCollapsedCard(attachments);

  return (
    <div
      className={`
        group relative overflow-hidden border transition-colors duration-150
        ${POSITION_CLASS[position]}
        ${isSelected ? 'border-accent/30 bg-accent/[0.07]' : 'border-white/8 bg-white/2.5'}
      `}
    >
      <EventStarButton
        event={event}
        isHighlighted={isHighlighted}
        canToggleHighlight={canToggleHighlight}
        onToggleHighlight={onToggleHighlight}
      />
      <EventCardCollapsed
        event={event}
        isSelected={isSelected}
        color={color}
        label={label}
        Icon={Icon}
        attachmentsLength={attachments.length}
        attachmentsLabel={attachmentsLabel}
        attachments={collapsedAttachments}
        onSelect={onSelect}
        onDeselect={onDeselect}
      />
      {isSelected && <EventCardExpandedPanel expandedEvent={expandedEvent} />}
    </div>
  );
}
